import { useMemo } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { useAtomValue } from 'jotai';

import {
  type LevelConfiguration,
  levelExtensionAtom,
  tournamentLevelConfigAtom,
  tournamentLevelConfigAtomFamily,
} from 'atoms/tournament';
import { type EventFormat } from 'components/dialogs/draw/addDraw/utils';
import { useTournamentContext } from 'hooks/tournament/useTournamentContext';
import { isStandalone } from 'utils/environment';

const { DOUBLES, TEAM } = EventTypeEnum;

export interface LevelConfigFormats {
  eventFormats: EventFormat[];
  scoreFormats: string[];
}

export const useLevelConfig = (tournamentId?: string) => {
  const levelConfig = useAtomValue(
    tournamentId ? tournamentLevelConfigAtomFamily(tournamentId) : tournamentLevelConfigAtom,
  );
  const levelExtension = useAtomValue(levelExtensionAtom);

  return useMemo(() => {
    const { levelConfigurationOverrides: overrides, currentOrgLevelConfiguration: fallback } = levelExtension ?? {};
    const config = levelConfig || fallback || {};
    return { ...config, ...overrides };
  }, [levelConfig, levelExtension]);
};

const getRestrictedEventFormats = (eventType: EventTypeEnum, levelConfig: LevelConfiguration): EventFormat[] => {
  if (eventType === DOUBLES) return levelConfig.doublesEventFormats;
  if (eventType === TEAM) return [];
  return levelConfig.eventFormats;
};

const getRestrictedScoreFormats = (eventType: EventTypeEnum, levelConfig: LevelConfiguration): string[] => {
  if (eventType === DOUBLES) return levelConfig.doublesScoreFormats;
  if (eventType === TEAM) return levelConfig.teamScoreFormats;
  return levelConfig.scoreFormats;
};

export const useLevelConfigFormats = (eventType: EventTypeEnum): LevelConfigFormats => {
  const { isAdminOrSuperAdmin } = useTournamentContext();
  const levelConfig = useLevelConfig();

  const currentUser = JSON.parse(localStorage.getItem('authenticatedUser'));
  const isGlobalAdmin = currentUser?.user?.SystemRoles === 1;

  const restrictedEventFormats = getRestrictedEventFormats(eventType, levelConfig);
  const restrictedScoreFormats = getRestrictedScoreFormats(eventType, levelConfig);

  // Empty arrays mean no restriction to be applied, all options will be available
  if (isStandalone() || !restrictedEventFormats || !restrictedScoreFormats || isGlobalAdmin) {
    return { eventFormats: [], scoreFormats: [] };
  }
  if (isAdminOrSuperAdmin) return { eventFormats: restrictedEventFormats, scoreFormats: [] };
  return { eventFormats: restrictedEventFormats, scoreFormats: restrictedScoreFormats };
};
